import { useEffect, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa } from "../game/cards";
import { WalletBar } from "./Wallet";
import { useRegistered, authHref } from "./Account";
import { notify } from "./Dialog";

/**
 * فروشگاه — فرش، پشتِ کارت، و کارت‌های ویژه.
 *
 * تا امروز وب هیچ فروشگاهی نداشت: کاتالوگ فقط روی سوکت و بعد از Welcome به
 * اپ می‌رسید، و هر قلمی که در پنل ساخته می‌شد در وب دیده نمی‌شد.
 *
 * فهرست از `GET /v1/catalog` می‌آید — نام، تصویر و **بهای مؤثر** (با تخفیف
 * و بازنویسیِ سکو). خرید روی همان سوکتِ بازی است، چون کیف آن‌جاست.
 *
 * چیزی که این‌جا **نیست**: سکه و گوهر. آن‌ها خریدِ درون‌برنامه‌ای‌اند و
 * فعلاً فقط در اپِ اندروید.
 */
type CatalogItem = {
  id: string; category: string; name?: string; image?: string;
  kind: string; price: number; was?: number; cap?: number;
};

const TABS = [
  { key: "back", fa: "پشتِ کارت" },
  { key: "carpet", fa: "فرش‌ها" },
  { key: "special", fa: "کارت‌های ویژه" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function Shop({ onBack }: { onBack: () => void }) {
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [tab, setTab] = useState<TabKey>("back");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  // پشتِ به‌کاربسته از سرور می‌آید و نه از این مرورگر: روی گوشی و مرورگر
  // باید یکی باشد.
  const [equipped, setEquipped] = useState("");
  const wallet = useSignal(client.wallet);
  const inventory = useSignal(client.inventory);
  const registered = useRegistered();

  useEffect(() => {
    let alive = true;
    fetch("/v1/catalog", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("http"))))
      .then((j: { items: CatalogItem[] }) => { if (alive) setItems(j.items ?? []); })
      .catch(() => { if (alive) setErr("فهرستِ فروشگاه نیامد؛ بعداً دوباره امتحان کن."); });
    void client.rest<{ back?: string }>("GET", "/v1/me")
      .then((me) => { if (alive) setEquipped(me?.back ?? ""); })
      .catch(() => { /* مهمان یا شبکه — پشتِ پیش‌فرض */ });
    return () => { alive = false; };
  }, []);

  async function buy(it: CatalogItem) {
    if (busy) return;
    setBusy(it.id);
    const e = await client.buyItem(it.id);
    setBusy("");
    if (e) { notify(buyError(e)); return; }
    notify("خریدی — مبارک باشد");
  }

  async function equip(id: string) {
    if (busy) return;
    setBusy(id);
    const e = await client.setBack(id);
    setBusy("");
    if (e) { notify(backError(e)); return; }
    setEquipped(id);
    notify(id ? "پشتِ کارتت عوض شد" : "برگشتی به پشتِ پیش‌فرض");
  }

  const shown = (items ?? []).filter((i) => i.category === tab);
  const coins = wallet?.coins ?? 0n;

  return (
    <div className="page stack">
      <div className="row">
        <button className="hb" onClick={onBack}>← خانه</button>
        <b style={{ flex: 1 }}>فروشگاه</b>
      </div>
      <WalletBar />

      <div className="row" style={{ gap: 8 }} role="group" aria-label="دسته">
        {TABS.map((t) => (
          <button key={t.key} className={"btn ghost" + (tab === t.key ? " on" : "")}
                  aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
            {t.fa}
          </button>
        ))}
      </div>

      {!registered && (
        <div className="plate stack">
          <div className="muted">خرید حساب می‌خواهد — سکه و قلم‌هایت باید جایی بمانند که با گوشیِ دیگری هم برگردند.</div>
          <a className="btn" href={authHref("login")}>ورود / ثبت‌نام</a>
        </div>
      )}

      {tab === "carpet" && (
        <div className="muted" style={{ fontSize: 13 }}>
          فرشِ میز را میزبان انتخاب می‌کند و هر چهار نفر یکی می‌بینند. فرش‌های شهری فعلاً در اپ دیده می‌شوند.
        </div>
      )}
      {tab === "back" && (
        <div className="muted" style={{ fontSize: 13 }}>
          پشتِ کارت مالِ خودت است: ورق‌هایت را هم‌بازی‌ها با همین می‌بینند.
        </div>
      )}

      {err && <div className="plate">{err}</div>}
      {!items && !err && <div className="muted">…</div>}
      {items && shown.length === 0 && !err && <div className="muted">در این دسته چیزی برای فروش نیست.</div>}

      <div className="shopgrid">
        {shown.map((it) => (
          <Card
            key={it.id}
            item={it}
            owned={inventory.get(it.id) ?? 0}
            coins={coins}
            busy={busy === it.id}
            canBuy={registered}
            equipped={equipped === it.id}
            onBuy={() => void buy(it)}
            onEquip={() => void equip(it.id)}
          />
        ))}
      </div>

      {tab === "back" && (
        <button className="btn ghost" disabled={!equipped || !!busy} onClick={() => void equip("")}>
          برگرد به پشتِ پیش‌فرض
        </button>
      )}
    </div>
  );
}

function Card({ item, owned, coins, busy, canBuy, equipped, onBuy, onEquip }: {
  item: CatalogItem; owned: number; coins: bigint; busy: boolean; canBuy: boolean; equipped: boolean;
  onBuy: () => void; onEquip: () => void;
}) {
  const consumable = item.kind === "consumable";
  const cap = item.cap ?? 0;
  const full = consumable ? owned >= cap : owned > 0;
  const poor = coins < BigInt(item.price);

  return (
    <div className="plate stack shopcard">
      <div className="shopart">
        {item.image
          ? <img src={item.image} alt="" loading="lazy" onError={hideImg} />
          : <span className="muted" style={{ fontSize: 12 }}>بی‌تصویر</span>}
      </div>
      <b>{item.name || item.id}</b>
      {consumable && <div className="muted" style={{ fontSize: 12 }}>{fa(owned)} از {fa(cap)}</div>}

      {full && !consumable ? (
        item.category === "back"
          ? <button className="btn" disabled={busy || equipped} onClick={onEquip}>
              {equipped ? "به‌کار بسته" : "به‌کار ببند"}
            </button>
          : <span className="btn ghost" aria-disabled="true">داری</span>
      ) : full ? (
        <span className="btn ghost" aria-disabled="true">سقفش همین است</span>
      ) : (
        <button className="btn" disabled={busy || !canBuy || poor} onClick={onBuy}
                title={poor ? "سکه‌ات کافی نیست" : undefined}>
          {busy ? "…" : <>{item.was ? <s className="muted">{fa(item.was)}</s> : null} {fa(item.price)} سکه</>}
        </button>
      )}
    </div>
  );
}

const hideImg = (e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.visibility = "hidden"; };

function buyError(code: string): string {
  switch (code) {
    case "insufficient_coins": return "سکه‌ات کافی نیست";
    case "already_owned":      return "این را از قبل داری";
    case "item_off":           return "این قلم موقتاً فروخته نمی‌شود";
    case "unknown_item":       return "این قلم دیگر نیست";
    case "offline":            return "وصل نیستی؛ چند لحظه";
    case "network":            return "به سرور نرسید؛ دوباره امتحان کن";
    default:                   return "نشد؛ دوباره امتحان کن";
  }
}

function backError(code: string): string {
  switch (code) {
    case "not_owned":   return "اول باید بخری‌اش";
    case "not_a_back":  return "این پشتِ کارت نیست";
    case "unauthorized": return "اول وارد شو";
    default:            return "نشد؛ دوباره امتحان کن";
  }
}
